<template lang="pug">
  b-form.form-horizontal(novalidate @submit.prevent="updatePassword" :disabled="!submission.success")
    .form-group
      label {{ $t("ui.old_password") }}
      b-input-group.input-group-sm
        DebounceInput(
          :type=" (isOPWShown) ? 'password' : 'text'"
          ref="old_password"
          :dirty="$v.old_password.$dirty"
          :error="$v.old_password.$error"
          :placeholder='$t("ui.old_password")'
          v-model.trim.lazy="$v.old_password.$model"
          :maxLength="$v.old_password.$params.maxLength.max"
          :disabled="loading"
        )
        .input-group-append(style="cursor: pointer;")
          b-button.btn.btn-outline-secondary.btn-sm(:pressed.sync="isOPWShown" style="border-top-right-radius: 3px;border-bottom-right-radius: 3px;")
            i.fa(:class=" (isOPWShown) ? 'icon-eye-open fa-eye' : 'icon-eye-close fa-eye-slash'")
        ValidateResult(:value="$v.old_password" :name="$t('ui.old_password')")
    .form-group
      .d-flex.align-items-center.justify-content-between.mb-1
        label.mb-0 {{ $t("ui.new_password") }}
        small(v-if="new_password" :class="passwordStrength.badgeClass") {{ passwordStrength.strength }} ({{ passwordStrength.score }}/100)
      b-input-group.input-group-sm
        DebounceInput(
          :type=" (isPWShown) ? 'password' : 'text'"
          ref="new_password"
          :dirty="$v.new_password.$dirty"
          :error="$v.new_password.$error"
          :placeholder='$t("ui.new_password")'
          v-model.trim.lazy="$v.new_password.$model"
          :maxLength="$v.new_password.$params.maxLength.max"
          :disabled="loading"
        )
        .input-group-append(style="cursor: pointer;")
          b-button.btn.btn-outline-secondary.btn-sm(:pressed.sync="isPWShown" style="border-top-right-radius: 0;border-bottom-right-radius: 0;")
            i.fa(:class=" (isPWShown) ? 'icon-eye-open fa-eye' : 'icon-eye-close fa-eye-slash'")
          PasswordRules
        ValidateResult(:value="$v.new_password" :name="$t('ui.new_password')" :errors="newPasswordErrors")
    .form-group
      label {{ $t("ui.confirm_password") }}
      b-input-group.input-group-sm
        DebounceInput(
          :type=" (isCPWShown) ? 'password' : 'text'"
          ref="confirm_password"
          :dirty="$v.confirm_password.$dirty"
          :error="$v.confirm_password.$error"
          :placeholder='$t("ui.confirm_password")'
          v-model.trim.lazy="$v.confirm_password.$model"
          :disabled="loading"
        )
        .input-group-append(style="cursor: pointer;")
          b-button.btn.btn-outline-secondary.btn-sm(:pressed.sync="isCPWShown" style="border-top-right-radius: 3px;border-bottom-right-radius: 3px;")
            i.fa(:class=" (isCPWShown) ? 'icon-eye-open fa-eye' : 'icon-eye-close fa-eye-slash'")
        ValidateResult(:value="$v.confirm_password" :name="$t('ui.confirm_password')" :name2="$t('ui.new_password')")
    .form-group
      b-form-group
        b-alert.alert-sm.animated.flash(:show="!submission.success" variant="danger") {{ $t(submission.status) }}
    .form-group
      b-form-group
        SubmitButton(:loading="loading" icon="fa fa-times" :text="$t('ui.reset')" isModal=false type="button"  @click="clear()" :disabled="$helpers.isSuspended()")
        SubmitButton.ml-2(:loading="loading" icon="fa fa-save" :text="$t('ui.save')" isModal=false type="button" @click="updatePassword()" :disabled="$helpers.isSuspended()")
</template>

<script>
import config from "@/config";
import { required, minLength, maxLength, sameAs, helpers } from "vuelidate/lib/validators";
import SubmitButton from "@/library/SubmitButton";
import ValidateResult from "@/library/ValidateResult";
import DebounceInput from "@/library/DebounceInput";
import PasswordRules from "@/library/PasswordRules";

// const containAlphaNum = helpers.regex("containAlphaNum", /^(?=.*\d)(?=.*[a-zA-Z])/);

export default {
  components: {
    SubmitButton,
    ValidateResult,
    DebounceInput,
    PasswordRules
  },
  data() {
    return {
      isOPWShown: true,
      isPWShown: true,
      isCPWShown: true,
      old_password: "",
      new_password: "",
      confirm_password: "",
      newPasswordErrors: [],
      passwordStrength: { score: 0, strength: "", badgeClass: "text-muted" },
      loading: false,
      submission: {
        success: true,
        status: ""
      },
      redirect: "/settings"
    }
  },
  validations: {
    old_password: {
      required,
      maxLength: maxLength(50)
    },
    new_password: {
      required,
      valid: function(value) {
        if (!helpers.req(value)) return true;
        return this.$helpers.validatePassword(value).isValid;
      },
      notSame: function(value) {
        return value != this.old_password;
      },
      // minLength: minLength(8),
      maxLength: maxLength(50)
    },
    confirm_password: {
      required,
      sameAs: sameAs('new_password')
    }
  },
  watch: {
    new_password(value) {
      if (!value) {
        this.newPasswordErrors = [];
        this.passwordStrength = { score: 0, strength: "", badgeClass: "text-muted" };
        return;
      }
      const result = this.$helpers.validatePassword(value);
      this.newPasswordErrors = result.errors;
      const strengthStyles = {
        Weak: { badgeClass: "text-danger" },
        Fair: { badgeClass: "text-warning" },
        Strong: { badgeClass: "text-primary" },
        "Very Strong": { badgeClass: "text-success" }
      };
      const style = strengthStyles[result.strength] || { badgeClass: "text-muted" };
      this.passwordStrength = {
        score: result.score,
        strength: result.strength,
        badgeClass: style.badgeClass
      };
    }
  },
  mounted () {
    this.$refs.old_password.$el.focus();
  },
  methods: {
    reset: function() {
      this.$v.$reset()
      this.submission.success = true
      this.submission.status = ""
    },
    clear: function() {
      this.old_password = ""
      this.new_password = ""
      this.confirm_password = ""
      this.passwordStrength = { score: 0, strength: "", badgeClass: "text-muted" };
      this.newPasswordErrors = [];
      this.$nextTick(() => {
        this.reset()
      });
    },
    updatePassword: function() {
      this.reset()
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loading = true
        this.$swal({
          title: this.$t("ui.status"),
          text: this.$t("message.update_account_password_confirm"),
          type: "warning",
          showCancelButton: true,
          allowOutsideClick: false
        }).then(result => {
          if (result.value) {
            let password = this.old_password
            let new_password = this.new_password
            this.$store
              .dispatch("user/updatePassword", { password, new_password })
              .then(response => {
                this.loading = false
                this.submission.success = response.success
                this.submission.status = this.$t("message." + response.errorTag);
                this.$swal({
                  title: this.$t("ui.succeed"),
                  text: this.$t("message.update_account_password_success"),
                  type: "success",
                  allowOutsideClick: false
                }).then(isConfirm => {
                  if (isConfirm) {
                    this.cancel();
                  }
                });
              })
              .catch(response => {
                this.loading = false
                this.submission.success = response.success
                this.submission.status = this.$t("message." + response.errorTag);
              });
          } else if (
            // Read more about handling dismissals
            result.dismiss === 'cancel'
          ) {
            this.loading = false
            this.$swal({
              title: this.$t("ui.cancel"),
              text: this.$t("message.update_account_password_cancel"),
              type: "error",
              allowOutsideClick: false
            }).then(isConfirm => {
              if (isConfirm) {
                this.cancel();
              }
            });
          }
        });
      }
    },
    cancel: function() {
      this.clear()
      this.$router.push("/settings").catch(err => {})
    }
  }
}
</script>